import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { editItem, removeItem, restoreItem } from '../../../data/commands/shopping';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { useGoBack } from '../../spending/hooks';
import { ItemDetailFields, draftOf, parseDraft, type ItemDraft } from '../components/ItemFields';
import { copy, errorText } from '../copy';
import { useItem } from '../hooks';
import { INPUT } from '../styles';

export function EditItemRoute() {
  const { id = '' } = useParams();
  const item = useItem(id);
  const goBack = useGoBack('/m/shopping');
  if (item === undefined) return null;
  if (item === null || item.deletedAt || item.tripId) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editTitle}>
        <p className="text-muted">{copy.notFound}</p>
      </Sheet>
    );
  }
  return <EditItemSheet id={id} initial={draftOf(item)} goBack={goBack} />;
}

function EditItemSheet({
  id,
  initial,
  goBack,
}: {
  id: string;
  initial: ItemDraft;
  goBack(): void;
}) {
  const env = useCommandEnv();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const patch = (p: Partial<ItemDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseDraft(draft);
    if (!parsed.ok) {
      setError(errorText(parsed.error));
      return;
    }
    const r = await editItem(env, id, parsed.input);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    goBack();
  }

  async function onRemove() {
    await removeItem(env, id);
    showToast({
      message: copy.removed(initial.name),
      action: { label: copy.undo, onPress: () => void restoreItem(env, id) },
    });
    goBack();
  }

  return (
    <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editTitle}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm text-muted">
          {copy.addLabel}
          <input
            value={draft.name}
            maxLength={80}
            onChange={(e) => patch({ name: e.target.value })}
            className={INPUT}
          />
        </label>
        <ItemDetailFields draft={draft} onChange={patch} />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block className="min-h-12">
          {copy.save}
        </Button>
        <Button variant="destructive" block onClick={() => void onRemove()}>
          {copy.remove}
        </Button>
      </form>
    </Sheet>
  );
}
